import React, { useState, lazy } from 'react';
import { XCircle, CheckCircle2, Network, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
// Representative imagery to give high visual value
const SIN_PRO_IMG = 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=600&h=400&fit=crop&q=70&auto=format';
// ^ frustrated worker with papers, isolated
const CON_PRO_IMG = 'https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=600&h=400&fit=crop&q=70&auto=format';
// ^ medical team coordination meeting
export const ProSection = () => {
  const [view, setView] = useState<'sin' | 'con'>('con');
  return <section className="py-12 md:py-20 bg-cream" data-id="element-248">
      <div className="max-w-5xl mx-auto px-4" data-id="element-249">
        <div className="text-center mb-8 md:mb-12" data-id="element-250">
          <h2 className="text-2xl md:text-4xl font-extrabold text-navy mb-2 md:mb-4 leading-tight" data-id="element-251">
            ¿Qué agrega el <span className="text-gold" data-id="element-252">PRO</span>?
          </h2>
          <p className="text-sm md:text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-253">
            Para TO que quieren ir un paso más allá — interdisciplina y gestión
          </p>
        </div>

        {/* Mobile: Toggle */}
        <div className="md:hidden mb-8" data-id="element-254">
          <div className="flex bg-white p-1 rounded-full border border-gray-200 mb-4 max-w-xs mx-auto shadow-sm" data-id="element-255">
            <button onClick={() => setView('sin')} className={`flex-1 py-2 text-xs font-bold rounded-full transition-colors ${view === 'sin' ? 'bg-danger text-white shadow' : 'text-gray-500'}`} data-id="element-256">
              Sin PRO
            </button>
            <button onClick={() => setView('con')} className={`flex-1 py-2 text-xs font-bold rounded-full transition-colors ${view === 'con' ? 'bg-navy text-gold shadow' : 'text-gray-500'}`} data-id="element-257">
              Con PRO
            </button>
          </div>

          <AnimatePresence mode="wait" data-id="element-258">
            {view === 'sin' ? <motion.div key="sin" initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -10
          }} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-259">
                <div className="relative h-40 overflow-hidden" data-id="element-260">
                  <img src={SIN_PRO_IMG} alt="TO trabajando solo sin coordinación de equipo" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover grayscale" data-id="element-261" />
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-white/60 to-transparent" data-id="element-262"></div>
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-danger text-white px-2.5 py-1 rounded-full text-[10px] font-bold shadow" data-id="element-263">
                    <XCircle className="w-3 h-3" data-id="element-264" /> SIN PRO
                  </div>
                </div>
                <div className="p-5" data-id="element-265">
                  <h4 className="text-base font-bold text-navy mb-2" data-id="element-266">
                    Trabajás solo y sin red de contención
                  </h4>
                  <p className="text-gray-600 text-sm leading-snug" data-id="element-267">
                    Coordinás con el kinesiólogo y el neurólogo pero cada uno
                    hace lo suyo sin articular. La familia recibe indicaciones
                    contradictorias.
                  </p>
                </div>
              </motion.div> : <motion.div key="con" initial={{
            opacity: 0,
            x: 10
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 10
          }} className="bg-navy rounded-2xl border border-gold shadow-lg text-white overflow-hidden" data-id="element-268">
                <div className="relative h-40 overflow-hidden" data-id="element-269">
                  <img src={CON_PRO_IMG} alt="Equipo interdisciplinario coordinándose en reunión clínica" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover" data-id="element-270" />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/60 to-transparent" data-id="element-271"></div>
                  <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-gold text-navy px-2.5 py-1 rounded-full text-[10px] font-bold shadow" data-id="element-272">
                    <CheckCircle2 className="w-3 h-3" data-id="element-273" /> CON PRO
                  </div>
                </div>
                <div className="p-5" data-id="element-274">
                  <h4 className="text-base font-bold mb-2" data-id="element-275">
                    Dejás de improvisar en equipo
                  </h4>
                  <p className="text-gray-300 text-sm leading-snug" data-id="element-276">
                    Sistema completo de coordinación interdisciplinaria: modelos
                    de reunión, protocolos por situación clínica, informe
                    unificado y plantillas de derivación que el médico realmente
                    responde.
                  </p>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* Desktop: Side-by-side with images */}
        <div className="hidden md:grid grid-cols-2 gap-8 mb-16" data-id="element-277">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-id="element-278">
            <div className="relative h-52 overflow-hidden" data-id="element-279">
              <img src={SIN_PRO_IMG} alt="TO trabajando solo sin coordinación de equipo" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover grayscale" data-id="element-280" />
              <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-white/20 to-transparent" data-id="element-281"></div>
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-danger text-white px-3 py-1.5 rounded-full text-xs font-bold shadow-lg" data-id="element-282">
                <XCircle className="w-4 h-4" data-id="element-283" /> SIN PRO
              </div>
            </div>
            <div className="p-7" data-id="element-284">
              <h4 className="text-xl font-bold text-navy mb-3" data-id="element-285">
                Trabajás solo y sin red de contención
              </h4>
              <p className="text-gray-600 leading-relaxed" data-id="element-286">
                Coordinás con el kinesiólogo y el neurólogo pero cada uno hace
                lo suyo sin articular. La familia recibe indicaciones
                contradictorias.
              </p>
            </div>
          </div>

          <div className="bg-navy rounded-2xl border border-gold shadow-2xl shadow-gold/10 text-white overflow-hidden hover:shadow-gold/20 transition-shadow" data-id="element-287">
            <div className="relative h-52 overflow-hidden" data-id="element-288">
              <img src={CON_PRO_IMG} alt="Equipo interdisciplinario coordinándose en reunión clínica" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover" data-id="element-289" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" data-id="element-290"></div>
              <div className="absolute top-4 left-4 flex items-center gap-2 bg-gold text-navy px-3 py-1.5 rounded-full text-xs font-bold shadow-lg" data-id="element-291">
                <CheckCircle2 className="w-4 h-4" data-id="element-292" /> CON PRO
              </div>
            </div>
            <div className="p-7" data-id="element-293">
              <h4 className="text-xl font-bold mb-3" data-id="element-294">
                Dejás de improvisar en equipo
              </h4>
              <p className="text-gray-300 leading-relaxed" data-id="element-295">
                Sistema completo de coordinación interdisciplinaria: modelos de
                reunión, protocolos por situación clínica, informe unificado y
                plantillas de derivación que el médico realmente responde.
              </p>
            </div>
          </div>
        </div>

        <div className="text-center mb-4 md:mb-8" data-id="element-296">
          <h3 className="text-lg md:text-2xl font-bold text-navy" data-id="element-297">
            EL PRO INCLUYE:
          </h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6" data-id="element-298">
          <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 flex gap-3 md:gap-4 items-start hover:border-teal/30 hover:shadow transition-all" data-id="element-299">
            <div className="bg-teal/10 p-2 md:p-3 rounded-lg flex-shrink-0" data-id="element-300">
              <Network className="w-5 h-5 md:w-6 md:h-6 text-teal" data-id="element-301" />
            </div>
            <div data-id="element-302">
              <h4 className="font-bold text-navy text-sm md:text-lg mb-1 md:mb-2" data-id="element-303">
                PRO 1: Abordaje Interdisciplinario
              </h4>
              <p className="text-gray-600 text-xs md:text-sm" data-id="element-304">
                Coordinación con Kinesiología, Fonoaudiología, Neurología y
                Familia. El TO como eje del equipo.
              </p>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-200 flex gap-3 md:gap-4 items-start hover:border-gold/30 hover:shadow transition-all" data-id="element-305">
            <div className="bg-gold/10 p-2 md:p-3 rounded-lg flex-shrink-0" data-id="element-306">
              <Briefcase className="w-5 h-5 md:w-6 md:h-6 text-gold-dark" data-id="element-307" />
            </div>
            <div data-id="element-308">
              <h4 className="font-bold text-navy text-sm md:text-lg mb-1 md:mb-2" data-id="element-309">
                PRO 2: Gestión de tu Práctica
              </h4>
              <p className="text-gray-600 text-xs md:text-sm" data-id="element-310">
                Honorarios, cartera de pacientes, facturación, PAMI, reputación
                y crecimiento sostenible.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
};